// Shared date/time formatting for NoticeIA, in Paris (France) time.

// Backend timestamps are UTC but the isoformat() string carries no tz marker, so
// `new Date("...T14:12:36")` would read it as LOCAL time. Append 'Z' when no
// offset is present so it's parsed as UTC.
export function parseUtc(iso: string): Date {
  const hasTz = /([zZ])|([+-]\d\d:?\d\d)$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
}

// English ordinal suffix for a day number (1 -> "st", 2 -> "nd", 7 -> "th").
export function ordinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

// The day alone, in Paris time, e.g. "7th April 2026". Localized month; the
// ordinal day is English-only (other locales just show the number).
export function formatDay(iso: string, language: string): string {
  const d = parseUtc(iso);
  const tz: Intl.DateTimeFormatOptions = { timeZone: 'Europe/Paris' };
  const day = d.toLocaleString(language, { ...tz, day: 'numeric' });
  const month = d.toLocaleString(language, { ...tz, month: 'long' });
  const year = d.toLocaleString(language, { ...tz, year: 'numeric' });
  const dayLabel = language.startsWith('en') ? `${day}${ordinalSuffix(Number(day))}` : day;
  return `${dayLabel} ${month} ${year}`;
}

// Full, human date/time in Paris time, e.g. "7th April 2026, 2:04:26 PM".
export function formatWhen(iso: string, language: string): string {
  const time = parseUtc(iso).toLocaleString(language, {
    timeZone: 'Europe/Paris',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${formatDay(iso, language)}, ${time}`;
}

// Short date and time in Paris time, e.g. "12 Sep, 9:12 AM". For a line that only needs
// to place something in the past — a failure a later run has already answered — where the
// full form would be longer than the message it is stamping.
export function formatShortWhen(iso: string, language: string): string {
  return parseUtc(iso).toLocaleString(language, {
    timeZone: 'Europe/Paris',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
